import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Grid,
    DialogActions,
    DialogContent,
    TextField,
    CircularProgress,
    Button,
    MenuItem,
    Box
} from "@mui/material";
import { useSnackbar } from "notistack";
import http from "src/utils/httpHelper";
import User from "src/models/user";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";

interface UsersFormProps {
    modalCloseCallback,
    onSuccessCallback
    item?: User
}

const UsersForm = ({ modalCloseCallback, onSuccessCallback, item }: UsersFormProps) => {
    const { t }: { t: any } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [roles, setRoles] = useState([]);

    useEffect(() => {
        http.get('api/roles/for-select')
            .then((response) => setRoles(response.data))
            .catch(error => {
                enqueueSnackbar(error.message, { variant: 'error' });
            });
    }, [enqueueSnackbar]);

    const { handleSubmit, formState: { errors, isSubmitting, isValid }, control, reset } = useForm({
        mode: 'onTouched',
        resolver: yupResolver(User.getRules(item)),
        defaultValues: User.getInitValues(item)
    });

    return (
        <form noValidate onSubmit={handleSubmit(async (values) => {
            await http.request({
                method: item ? 'put' : 'post',
                url: item ? `/api/users/${item.id}` : '/api/users',
                data: values
            })
                .then((response) => {
                    onSuccessCallback();
                    enqueueSnackbar(response.data.message, { variant: 'success' });
                })
                .catch((error) => {
                    console.error(error);
                    enqueueSnackbar(error.message, { variant: 'error' });
                });
        })}>
            <DialogContent
                dividers
                sx={{
                    p: 3,
                }}
            >
                <Grid container spacing={3}>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.name)}
                                    fullWidth
                                    helperText={errors.name?.message}
                                    label={t("FIELDS.NAME")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.email)}
                                    fullWidth
                                    helperText={errors.email?.message}
                                    label={t("FIELDS.EMAIL")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        {
                            <Controller
                                name="role_id"
                                control={control}
                                render={({ field, field: { value } }) => (
                                    <TextField
                                        fullWidth={true}
                                        select
                                        label={t('FIELDS.ROLE_ID')}
                                        {...field}
                                        value={roles.length ? value : ''}
                                    >
                                        {roles.map((role) => (
                                            <MenuItem key={role.id} value={role.id}>
                                                {role.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        }
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="password"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.password)}
                                    fullWidth
                                    helperText={errors.password?.message}
                                    label={t("FIELDS.PASSWORD")}
                                    type="password"
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="password_confirmation"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.password_confirmation)}
                                    fullWidth
                                    helperText={errors.password_confirmation?.message}
                                    label={t("FIELDS.PASSWORD_CONFIRMATION")}
                                    type="password"
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="active"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    fullWidth={true}
                                    select
                                    label={t('FIELDS.ACTIVE')}
                                    {...field}
                                >
                                    <MenuItem value={1} selected={true}>
                                        {t('USERS.ACTIVE.1')}
                                    </MenuItem>
                                    <MenuItem value={0}>
                                        {t('USERS.ACTIVE.0')}
                                    </MenuItem>
                                </TextField>
                            )}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                sx={{
                    p: 3,
                    display: 'flex',
                    justifyContent: 'flex-end'
                }}
            >
                <Box>
                    <Button color="secondary" onClick={modalCloseCallback}>
                        {t("GENERAL.CANCEL")}
                    </Button>
                    <Button
                        sx={{
                            ml: 2
                        }}
                        type="submit"
                        startIcon={
                            isSubmitting ? <CircularProgress size="1rem" /> : null
                        }
                        disabled={isSubmitting || !isValid}
                        variant="contained"
                    >
                        {t(!item ? "USERS.CREATE" : 'USERS.EDIT')}
                    </Button>
                </Box>
            </DialogActions>
        </form>
    )
}

export default UsersForm;